import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  medium: { label: 'Medium', color: 'text-amber-600', bg: 'bg-amber-100' },
  high: { label: 'High', color: 'text-orange-600', bg: 'bg-orange-100' },
  critical: { label: 'Critical', color: 'text-red-600', bg: 'bg-red-100' },
};

const SNAG_STATUS_CONFIG = {
  open: { label: 'Open', color: 'text-amber-600', bg: 'bg-amber-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-100' },
  resolved: { label: 'Resolved', color: 'text-emerald-600', bg: 'bg-emerald-100' },
};

const AUDIT_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-slate-600', bg: 'bg-slate-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-100' },
  completed: { label: 'Completed', color: 'text-emerald-600', bg: 'bg-emerald-100' },
};

const DOC_STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'text-emerald-600', bg: 'bg-emerald-100' },
  'expiring-soon': { label: 'Expiring Soon', color: 'text-amber-600', bg: 'bg-amber-100' },
  'expiring-critical': { label: 'Critical', color: 'text-orange-600', bg: 'bg-orange-100' },
  expired: { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100' },
};

const SummaryCard = ({ icon, label, value, color, bg }) => (
  <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={18} className={color} />
    </div>
    <div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  </div>
);

const ClientComplianceReport = ({ siteId, siteName }) => {
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('audits');

  useEffect(() => {
    if (siteId) loadCompliance();
  }, [siteId]);

  const loadCompliance = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await clientService?.getSiteCompliance(siteId);
      if (err) throw err;
      setCompliance(data);
    } catch (err) {
      console.error('[ClientComplianceReport] load compliance failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading compliance data: ${err?.message}`);
      } else {
        setError(`Failed to load compliance data: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const openSnags = compliance?.snags?.filter(s => s?.status === 'open')?.length || 0;
  const resolvedSnags = compliance?.snags?.filter(s => s?.status === 'resolved')?.length || 0;
  const criticalIncidents = compliance?.incidents?.filter(i => i?.severity_level === 'critical' || i?.severity_level === 'high')?.length || 0;
  const expiredDocs = compliance?.documents?.filter(d => d?.status === 'expired' || d?.status === 'expiring-critical')?.length || 0;
  const completedAudits = compliance?.audits?.filter(a => a?.status === 'completed')?.length || 0;

  const SECTIONS = [
    { id: 'audits', label: 'Audits', icon: 'ClipboardCheck', count: compliance?.audits?.length || 0 },
    { id: 'snags', label: 'Snags', icon: 'AlertOctagon', count: compliance?.snags?.length || 0 },
    { id: 'incidents', label: 'Incidents', icon: 'ShieldAlert', count: compliance?.incidents?.length || 0 },
    { id: 'documents', label: 'Documents', icon: 'FileText', count: compliance?.documents?.length || 0 },
  ];

  return (
    <div className="space-y-5">
      {loading ? (
        <div className="bg-card rounded-xl border border-border p-8 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            <p className="text-sm text-muted-foreground">Loading compliance data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Icon name="AlertCircle" size={28} className="text-red-400 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard icon="ClipboardCheck" label="Completed Audits" value={completedAudits} color="text-emerald-600" bg="bg-emerald-100" />
            <SummaryCard icon="AlertOctagon" label="Open Snags" value={openSnags} color="text-amber-600" bg="bg-amber-100" />
            <SummaryCard icon="ShieldAlert" label="High/Critical Incidents" value={criticalIncidents} color="text-red-600" bg="bg-red-100" />
            <SummaryCard icon="FileX" label="Docs Expiring/Expired" value={expiredDocs} color="text-orange-600" bg="bg-orange-100" />
          </div>

          {/* Section Tabs */}
          <div className="bg-card rounded-xl border border-border">
            <div className="flex border-b border-border overflow-x-auto">
              {SECTIONS?.map(section => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeSection === section?.id
                      ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={section?.icon} size={15} />
                  {section?.label}
                  <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">{section?.count}</span>
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* Audits */}
              {activeSection === 'audits' && (
                compliance?.audits?.length === 0 ? (
                  <div className="text-center py-10">
                    <Icon name="ClipboardCheck" size={28} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No audits recorded for this site.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {compliance?.audits?.map(audit => {
                      const statusCfg = AUDIT_STATUS_CONFIG?.[audit?.status] || AUDIT_STATUS_CONFIG?.draft;
                      return (
                        <div key={audit?.id} className="flex items-start gap-3 p-4 rounded-xl border border-border">
                          <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="ClipboardCheck" size={16} className="text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-semibold text-foreground">{audit?.audit_name}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg?.bg} ${statusCfg?.color}`}>
                                {statusCfg?.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              {audit?.auditor_name && (
                                <span className="text-xs text-muted-foreground">By: {audit?.auditor_name}</span>
                              )}
                              {audit?.audit_date && (
                                <span className="text-xs text-muted-foreground">
                                  {new Date(audit?.audit_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                              {audit?.overall_result && (
                                <span className="text-xs font-medium text-foreground">Result: {audit?.overall_result}</span>
                              )}
                            </div>
                            {audit?.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{audit?.notes}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Snags */}
              {activeSection === 'snags' && (
                compliance?.snags?.length === 0 ? (
                  <div className="text-center py-10">
                    <Icon name="AlertOctagon" size={28} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No snags recorded for this site.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {compliance?.snags?.map(snag => {
                      const statusCfg = SNAG_STATUS_CONFIG?.[snag?.status] || SNAG_STATUS_CONFIG?.open;
                      return (
                        <div key={snag?.id} className="flex items-start gap-3 p-4 rounded-xl border border-border">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="AlertOctagon" size={16} className="text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-semibold text-foreground">{snag?.title}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg?.bg} ${statusCfg?.color}`}>
                                {statusCfg?.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">
                                Raised: {new Date(snag?.created_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              {snag?.resolved_at && (
                                <span className="text-xs text-emerald-600">
                                  Resolved: {new Date(snag?.resolved_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Incidents */}
              {activeSection === 'incidents' && (
                compliance?.incidents?.length === 0 ? (
                  <div className="text-center py-10">
                    <Icon name="ShieldAlert" size={28} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No incidents recorded for this site.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {compliance?.incidents?.map(incident => {
                      const sevCfg = SEVERITY_CONFIG?.[incident?.severity_level] || SEVERITY_CONFIG?.low;
                      return (
                        <div key={incident?.id} className="flex items-start gap-3 p-4 rounded-xl border border-border">
                          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="ShieldAlert" size={16} className="text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-semibold text-foreground capitalize">
                                {incident?.incident_type?.replace('_', ' ')}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sevCfg?.bg} ${sevCfg?.color}`}>
                                {sevCfg?.label}
                              </span>
                            </div>
                            {incident?.description && (
                              <p className="text-xs text-muted-foreground mb-1 line-clamp-2">{incident?.description}</p>
                            )}
                            {incident?.incident_date && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(incident?.incident_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Documents */}
              {activeSection === 'documents' && (
                compliance?.documents?.length === 0 ? (
                  <div className="text-center py-10">
                    <Icon name="FileText" size={28} className="text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No documents for this site.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {compliance?.documents?.map(doc => {
                      const statusCfg = DOC_STATUS_CONFIG?.[doc?.status] || DOC_STATUS_CONFIG?.valid;
                      return (
                        <div key={doc?.id} className="flex items-center gap-3 p-4 rounded-xl border border-border">
                          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="FileText" size={16} className="text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-semibold text-foreground truncate">{doc?.title}</p>
                              <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg?.bg} ${statusCfg?.color}`}>
                                {statusCfg?.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground capitalize">{doc?.category}</span>
                              {doc?.expiry_date && (
                                <span className="text-xs text-muted-foreground">
                                  Expires: {new Date(doc?.expiry_date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientComplianceReport;
